/**
 * Foyer - core domain types.
 *
 * These types are the contract between API, kiosk and admin UI. They are kept
 * free of any persistence or transport concern on purpose: the routing engine,
 * the form engine and the notification templating are pure functions over these
 * structures and are unit-testable without a database.
 */
/**
 * Languages a tablet can actually display.
 *
 * The list is deliberately not "every language a code exists for": a language
 * only belongs here once the kiosk ships a complete set of strings for it,
 * because an administrator who enables one expects to see it on the tablet.
 * `rtl` drives the text direction the kiosk applies to the whole document.
 * `flag` is the country (ISO 3166-1 alpha-2) whose flag the kiosk shows next
 * to the name - a visitor who cannot read German finds their language by the
 * picture first. The name always stays, because a flag stands for a country.
 */
export const SUPPORTED_LANGUAGES = [
    { code: 'de', label: 'Deutsch', rtl: false, flag: 'de' },
    { code: 'en', label: 'English', rtl: false, flag: 'gb' },
    { code: 'tr', label: 'Türkçe', rtl: false, flag: 'tr' },
    { code: 'fr', label: 'Français', rtl: false, flag: 'fr' },
    { code: 'nl', label: 'Nederlands', rtl: false, flag: 'nl' },
    { code: 'ar', label: 'العربية', rtl: true, flag: 'sa' },
    { code: 'ro', label: 'Română', rtl: false, flag: 'ro' },
    { code: 'it', label: 'Italiano', rtl: false, flag: 'it' },
    { code: 'ru', label: 'Русский', rtl: false, flag: 'ru' },
];
/**
 * Languages the *administration* is translated into.
 *
 * A subset of the list above, and deliberately so: the kiosk speaks whatever a
 * visitor in the lobby might speak, while the administration is used by the
 * staff of the customer. Adding a language here means writing and maintaining
 * a full catalogue of several hundred strings, so the list only grows when a
 * customer actually needs it.
 */
export const ADMIN_LANGUAGES = ['de', 'en', 'tr', 'fr', 'nl'];
export function isAdminLanguage(language) {
    return ADMIN_LANGUAGES.includes(language);
}
export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((entry) => entry.code);
export function isRightToLeft(language) {
    return SUPPORTED_LANGUAGES.some((entry) => entry.code === language && entry.rtl);
}
/** Human readable name of a language, falling back to the raw code. */
export function languageLabel(language) {
    return SUPPORTED_LANGUAGES.find((entry) => entry.code === language)?.label ?? language;
}
/** Country code of the flag shown next to a language, or null for an unknown one. */
export function languageFlag(language) {
    return SUPPORTED_LANGUAGES.find((entry) => entry.code === language)?.flag ?? null;
}
/* -------------------------------------------------------------------- roles */
export const ROLES = ['super_admin', 'tenant_admin', 'location_admin', 'staff', 'read_only'];
/* ------------------------------------------------------------------ visitors */
/**
 * Built-in visitor type keys. Tenants may add their own keys (e.g. `craftsman`,
 * `delivery`), therefore the transported value is a plain string and these
 * constants only describe the defaults shipped with a new tenant.
 */
export const BUILTIN_VISITOR_TYPES = ['tenant', 'prospect', 'guest'];
export const VISITOR_STATUSES = ['waiting', 'seen', 'in_progress', 'done', 'expired'];
export const NOTIFICATION_STATUSES = ['created', 'pending', 'sent', 'delivered', 'failed'];
export const PROVIDER_KINDS = ['webhook', 'push', 'email', 'teams', 'sms', 'mock'];
/* ---------------------------------------------------------------- form model */
export const FIELD_TYPES = [
    'text',
    'textarea',
    'phone',
    'email',
    'number',
    'date',
    'select',
    'radio',
    'checkbox',
    'multiselect',
    'search_select',
    'list_ref',
    'info',
];
/**
 * Field types that ask for nothing. An `info` field only shows a text (and a QR
 * code for its `linkUrl`), so it never carries an answer, is never required and
 * never appears in a notification.
 */
export const DISPLAY_ONLY_FIELD_TYPES = ['info'];
/**
 * What a `showWhen` may depend on besides another field: the reason picked
 * before the form, and the answer to "do you have an appointment?" (`yes` /
 * `no`). The `@` keeps them apart from field names, which can never contain it.
 */
export const FLOW_CONDITION_FIELDS = ['@reason', '@appointment'];
export const FIELD_VISIBILITIES = ['visible', 'hidden', 'disabled'];
/* ------------------------------------------------------------------- routing */
export const CONDITION_OPERATORS = [
    'eq',
    'neq',
    'in',
    'nin',
    'contains',
    'exists',
    'not_exists',
    'gt',
    'lt',
];
/**
 * Key of the dynamic group that always means "every active user of this
 * customer". Every tenant has one; it is created with the tenant and cannot be
 * emptied by an administrator, so a rule always has a safe audience to point at.
 */
export const ALL_USERS_GROUP_KEY = 'alle';
export const CLOSED_BEHAVIOURS = ['allow', 'standby_group', 'info_only', 'contact_form'];
/* ------------------------------------------------------------- feature flags */
export const FEATURE_FLAGS = [
    'ENABLE_TENANT_FLOW',
    'ENABLE_PROSPECT_FLOW',
    'ENABLE_GUEST_FLOW',
    'ENABLE_APPOINTMENTS',
    'ENABLE_ANALYTICS',
    'ENABLE_EXPORT',
    'ENABLE_EMAIL',
    'ENABLE_WEBHOOK',
    'ENABLE_PUSH',
    'ENABLE_TEAMS',
    'ENABLE_SMS',
    'ENABLE_QR_CHECKIN',
    'ENABLE_MULTI_LANGUAGE',
    'ENABLE_STAFF_VIEW',
];
//# sourceMappingURL=types.js.map